/**
 * Created by david.vanoni@coplex.com on 8/21/17.
 */

import { denormalize } from 'normalizr';
import { createSelector } from 'reselect';
import { file as fileSchema } from '../lib/schema.js';
import { selectEntities } from './entities.js';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';

// API Actions

const CREATE_FILE_REQUEST = 'changesync/files/CREATE_FILE_REQUEST';
const CREATE_FILE_SUCCESS = 'changesync/files/CREATE_FILE_SUCCESS';
const CREATE_FILE_FAILURE = 'changesync/files/CREATE_FILE_FAILURE';
const UPDATE_FILE_REQUEST = 'changesync/files/UPDATE_FILE_REQUEST';
const UPDATE_FILE_SUCCESS = 'changesync/files/UPDATE_FILE_SUCCESS';
const UPDATE_FILE_FAILURE = 'changesync/files/UPDATE_FILE_FAILURE';
const DELETE_FILE_REQUEST = 'changesync/files/DELETE_FILE_REQUEST';
const DELETE_FILE_SUCCESS = 'changesync/files/DELETE_FILE_SUCCESS';
const DELETE_FILE_FAILURE = 'changesync/files/DELETE_FILE_FAILURE';
const RETRIEVE_FILES_REQUEST = 'changesync/files/RETRIEVE_FILES_REQUEST';
const RETRIEVE_FILES_SUCCESS = 'changesync/files/RETRIEVE_FILES_SUCCESS';
const RETRIEVE_FILES_FAILURE = 'changesync/files/RETRIEVE_FILES_FAILURE';
const RETRIEVE_FILE_REQUEST = 'changesync/file/RETRIEVE_FILE_REQUEST';
const RETRIEVE_FILE_SUCCESS = 'changesync/file/RETRIEVE_FILE_SUCCESS';
const RETRIEVE_FILE_FAILURE = 'changesync/file/RETRIEVE_FILE_FAILURE';

// Search Actions
const SEARCH_FILES_REQUEST = 'changesync/files/SEARCH_FILES_REQUEST';
const SEARCH_FILES_SUCCESS = 'changesync/files/SEARCH_FILES_SUCCESS';
const SEARCH_FILES_FAILURE = 'changesync/files/SEARCH_FILES_FAILURE';
const UPDATE_FILE_SEARCH_QUERY = 'changesync/files/UPDATE_FILE_SEARCH_QUERY';
const SEARCH_FILES = 'changesync/files/SEARCH_FILES';
const CANCEL_FILE_SEARCH = 'changesync/files/CANCEL_FILE_SEARCH';

// Initial State

const initialState = {
  indexFileIds: [],
  searchQuery: '',
  pending: false,
  success: false,
};

// Reducer

export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_FILES_REQUEST:
      return {
        ...state,
        pending: true,
        success: false,
      };

    case RETRIEVE_FILES_SUCCESS:
      return {
        ...state,
        indexFileIds: action.payload.result,
        searchQuery: initialState.searchQuery,
        pending: false,
        success: true,
      };

    case SEARCH_FILES_REQUEST:
      return {
        ...state,
        pending: true,
        success: false,
      };

    case SEARCH_FILES_SUCCESS:
      return {
        ...state,
        indexFileIds: action.payload.result,
        pending: false,
        success: true,
      };

    case UPDATE_FILE_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload,
      };

    default:
      return state;
  }
};

// Action Creators

export const retrieveFiles =
  (projectId, initiativeType = null, initiativeId = null) =>
  (dispatch) => {
    let queryParams = {};

    if (initiativeType && initiativeId) {
      queryParams = {
        ...queryParams,
        initiativeableType: initiativeType,
        initiativeableId: initiativeId,
      };
    }

    dispatch({
      type: CANCEL_FILE_SEARCH,
      meta: {
        debounce: {
          cancel: true,
          key: SEARCH_FILES,
        },
      },
    });

    return dispatch({
      [CALL_API]: {
        types: [
          RETRIEVE_FILES_REQUEST,
          RETRIEVE_FILES_SUCCESS,
          RETRIEVE_FILES_FAILURE,
        ],
        endpoint: `projects/${projectId}/linked_files`,
        method: HTTP_METHODS.GET,
        schema: [fileSchema],
        query: queryParams,
      },
    });
  };

export const searchFiles = (projectId, query, initiativeType, initiativeId) => {
  let queryParams = { query };

  if (initiativeType && initiativeId) {
    queryParams = {
      ...queryParams,
      initiativeableType: initiativeType,
      initiativeableId: initiativeId,
    };
  }

  return {
    [CALL_API]: {
      types: [SEARCH_FILES_REQUEST, SEARCH_FILES_SUCCESS, SEARCH_FILES_FAILURE],
      endpoint: `projects/${projectId}/linked_files/search`,
      method: HTTP_METHODS.GET,
      schema: [fileSchema],
      query: queryParams,
    },
    meta: {
      debounce: {
        time: 300,
        key: SEARCH_FILES,
      },
    },
  };
};

export const updateFileSearchQuery = (query) => ({
  type: UPDATE_FILE_SEARCH_QUERY,
  payload: query,
});

export const retrieveFile = (fileId) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_FILE_REQUEST,
      RETRIEVE_FILE_SUCCESS,
      RETRIEVE_FILE_FAILURE,
    ],
    endpoint: `linked_files/${fileId}`,
    method: HTTP_METHODS.GET,
    schema: fileSchema,
  },
});

export const createFile = (file, projectId, initiativeType, initiativeId) => {
  if (initiativeType && initiativeId) {
    file.initiativeableType = initiativeType;
    file.initiativeableId = initiativeId;
  }

  return {
    [CALL_API]: {
      types: [CREATE_FILE_REQUEST, CREATE_FILE_SUCCESS, CREATE_FILE_FAILURE],
      endpoint: `projects/${projectId}/linked_files`,
      method: HTTP_METHODS.POST,
      schema: fileSchema,
      body: { linkedFile: file },
    },
  };
};

export const updateFile = (file) => ({
  [CALL_API]: {
    types: [UPDATE_FILE_REQUEST, UPDATE_FILE_SUCCESS, UPDATE_FILE_FAILURE],
    endpoint: `linked_files/${file.id}`,
    method: HTTP_METHODS.PUT,
    schema: fileSchema,
    body: { linked_file: file },
  },
});

export const deleteFile = (fileId) => ({
  [CALL_API]: {
    types: [DELETE_FILE_REQUEST, DELETE_FILE_SUCCESS, DELETE_FILE_FAILURE],
    endpoint: `linked_files/${fileId}`,
    method: HTTP_METHODS.DELETE,
  },
});

// Selectors

const selectIndexFileIds = () => (state) => state.files.indexFileIds;
export const selectFileSearchQuery = () => (state) => state.files.searchQuery;
export const selectIsSearching = (state) =>
  state.files.searchQuery !== initialState.searchQuery;
export const selectSearchRetrieveFilesState = (state) => state.files;

export const selectFiles = createSelector(
  selectEntities,
  selectIndexFileIds(),
  (entities, indexFileIds) => denormalize(indexFileIds, [fileSchema], entities)
);

export const selectFile = (fileId) =>
  createSelector(selectEntities, (entities) =>
    denormalize(fileId, fileSchema, entities)
  );
